import { transformRoute } from "helpers";
import { ContactRow } from "services/api/contacts/types";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import Badge from "ui/components/Badge/Badge";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { ReactSVG } from "react-svg";


export const CONTACT_REPRESENTATIVE_CHILD_COLUMNS = (
  t: (key: string) => string
): Column[] => [
    {
      title: "",
      key: "",
      field: "",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.name",
      key: "representative_name",
      field: "name",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.groups",
      key: "representative_groups",
      field: "",
      type: ColumnTypes.renderByCustomRow,
    },
    {
      title: "admin.contact.representative.role",
      key: "representative_role",
      field: "role",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.jobtitle",
      key: "representative_jobtitle",
      field: "jobTitle",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.department",
      key: "representative_department",
      field: "department",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.email",
      key: "representative_email",
      field: "email",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.phone",
      key: "representative_phone",
      field: "phone",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.representative.address",
      key: "representative_address",
      field: "address",
      type: ColumnTypes.string,
    },
  ];

export const CONTACT_SEARCH_COLUMNS = (
  t: (key: string) => string,
  handleDeleteClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handleEmailClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  handlePhoneClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    id: string
  ) => void,
  onGoogleMapClick: (
    event: React.MouseEvent<HTMLSpanElement>,
    latitude: number | null,
    longitude: number | null
  ) => void,
): Column[] => [
    {
      title: "",
      key: "contact_image",
      alwaysVisible: true,
      field: "image",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        const imageUrl = row.pictures && row.pictures.length > 0 ? row.pictures[0].url : null;
        const initials = row.contactType?.id === "C"
          ? (row.companyName ?? "").charAt(0).toUpperCase()
          : `${(row.firstName ?? "").charAt(0).toUpperCase()} ${(row.lastName ?? "").charAt(0).toUpperCase()}`;
        return <TableIcon imageUrl={imageUrl} initials={initials} />;
      },
    },
    {
      title: "admin.contact.search.name",
      key: "contact_name",
      alwaysVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        return (
          <div className="text-md">{row.name}</div>
        );
      },
    },
    {
      title: "admin.contact.search.contacttype",
      key: "contact_type",
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        const isCompany = row.contactType?.id === "C";
        return (
          <Badge
            text={isCompany ? "Tvrtka" : "Osoba"}
            variant={isCompany ? "info" : "success"}
            height="small"
            showDot={true}
          />
        );
      },
    },
    {
      title: "admin.contact.search.groups",
      key: "contact_groups",
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        return (
          <div className="flex flex-wrap gap-1">
            {row.contactGroups?.map((g) => (
              <Badge key={g.id} text={g.name} variant="info" height="small" />
            ))}
          </div>
        );
      },
    },
    {
      title: "admin.contact.search.jobtitle",
      key: "contact_jobtitle",
      field: "jobTitle",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.search.department",
      key: "contact_department",
      field: "department",
      type: ColumnTypes.string,
    },
    {
      title: "admin.contact.search.primaryemail",
      key: "contact_email",
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        return (
          <div>
            {row.primaryContactEmail && (
              <span
                onClick={(e) => handleEmailClick(e, row.primaryContactEmail.emailAddress)}
                className="inline-flex items-center gap-1.5 text-md font-medium truncate text-sky-600 hover:text-sky-700 cursor-pointer"
              >
                <ReactSVG src="/media/icons/email.svg" className="w-4 h-4 stroke-sky-500 shrink-0" />
                {row.primaryContactEmail?.emailAddress}
              </span>
            )}

          </div>
        );
      },
    },
    {
      title: "admin.contact.search.primaryphone",
      key: "contact_phone",
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        return (
          <div>
            {row.primaryContactPhone && (
              <span
                onClick={(e) => handlePhoneClick(e, row.primaryContactPhone.phoneNumber)}
                className="inline-flex items-center gap-1.5 text-md font-medium truncate text-sky-600 hover:text-sky-700 cursor-pointer"
              >
                <ReactSVG src="/media/icons/phone.svg" className="w-4 h-4 stroke-sky-500 shrink-0" />
                {row.primaryContactPhone?.phoneNumber}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "admin.contact.search.primaryaddress",
      key: "contact_address",
      field: "",
      type: ColumnTypes.custom,
      render: (row: ContactRow) => {
        return (
          <div>
            {row.primaryContactAddress && (
              <span
                onClick={(e) => onGoogleMapClick(e, row.primaryContactAddress.latitude, row.primaryContactAddress.longitude)}
                className="inline-flex items-center gap-1.5 text-md font-medium truncate text-sky-600 hover:text-sky-700 cursor-pointer"
              >
                <ReactSVG src="/media/icons/map.svg" className="w-4 h-4 stroke-sky-500 shrink-0" />
                {row.primaryContactAddress.line1}
              </span>
            )}


          </div>
        );
      },
    },
    {
      title: "",
      key: "contact_actions",
      alwaysVisible: true,
      field: "",
      type: ColumnTypes.custom,
      render: (row) => {
        return (
          <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
            <ButtonLink
              startIcon="/media/icons/info.svg"
              cssClassName="text-slate-600 hover:bg-blue-100"
              size="full"
              variant="text"
              to={transformRoute(ContactRoutes.ContactDetailPageRouter, {
                id: row.id,
              })}
            >
              Detalji kontakta
            </ButtonLink>
            {row.contactType?.id === "C" && (
              <ButtonLink
                startIcon="/media/icons/users.svg"
                cssClassName="text-slate-600 hover:bg-blue-100"
                size="full"
                variant="text"
                to={transformRoute(ContactRoutes.ContactRepresentativeSearchPageRouter, {
                  id: row.id,
                })}
              >
                Povezane osobe
              </ButtonLink>
            )}
             <ButtonLink
              startIcon="/media/icons/email.svg"
              cssClassName="text-slate-600 hover:bg-blue-100"
              size="full"
              variant="text"
              to={transformRoute(ContactRoutes.ContactEmailSearchPageRouter, {
                id: row.id,
              })}
            >
              Email kontakta
            </ButtonLink>
              <ButtonLink
              startIcon="/media/icons/phone.svg"
              cssClassName="text-slate-600 hover:bg-blue-100"
              size="full"
              variant="text"
              to={transformRoute(ContactRoutes.ContactPhoneSearchPageRouter, {
                id: row.id,
              })}
            >
              Telefoni kontakta
            </ButtonLink>
           
            <ButtonLink
              startIcon="/media/icons/map.svg"
              cssClassName="text-slate-600 hover:bg-blue-100"
              size="full"
              variant="text"
              to={transformRoute(ContactRoutes.ContactAddressSearchPageRouter, {
                id: row.id,
              })}
            >
              Adrese kontakta
            </ButtonLink>
          
            <ButtonLink
              startIcon="/media/icons/note.svg"
              cssClassName="text-slate-600 hover:bg-blue-100"
              size="full"
              variant="text"
              to={transformRoute(ContactRoutes.ContactNoteSearchPageRouter, {
                id: row.id,
              })}
            >
              Bilješke kontakta
            </ButtonLink>
            <ButtonMenuItem
              startIcon="/media/icons/trash.svg"
              variant="remove"
              className="text-slate-600 hover:bg-blue-100"
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              Obriši
            </ButtonMenuItem>
          </ButtonMenu>
        );
      },
    },
  ];